import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProductService, Product } from '../services/productService';
import { addToCart as backendAddToCart } from '../services/cartService';
import styles from '../styles/ProductDetails.module.css';

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | undefined>('');
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;

    // 🧹 Clear old data immediately to show new loading state
    setProduct(null);
    setSelectedImage(undefined);
    setSimilarProducts([]);
    setLoading(true);

    const fetchProduct = async () => {
      try {
        const productData = await ProductService.getProductById(id);
        setProduct(productData);
        setSelectedImage(productData.images?.[0]?.signedUrl);

        if (productData.category?.id) {
          const similarData = await ProductService.getProductsByCategory(
            productData.category.id.toString()
          );
          const filteredSimilar = similarData
            .filter((p) => p.id !== productData.id)
            .slice(0, 15);
          setSimilarProducts(filteredSimilar);
        }
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);


  const handleAddToCart = async () => {
    if (!product || adding) return;

    setAdding(true);
    try {
      await backendAddToCart(Number(product.id), 1);
      // notify header/other listeners that cart changed
      try {
        window.dispatchEvent(new Event('cartUpdated'));
      } catch {}
      alert('Product added to cart!');
    } catch (error) {
      console.error('Failed to add product to cart:', error);
      alert('Could not add product to cart. Please try again.');
    } finally {
      setAdding(false);
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (loading)
    return (
      <div className="loading">
        <div className="gear" />
        <p>Loading...</p>
      </div>
    );
  if (!product) return <div>Product not found.</div>;

  const renderImage = (url?: string, name?: string) =>
    url ? (
      <img src={url} alt={name} className={styles.productImage} />
    ) : (
      <div className={styles.productPlaceholder}>No Image</div>
    );

  return (
    <div className={styles.productDetailsContainer}>
      <div className={styles.productInfo}>
        <div className={styles.imageSection}>
          {renderImage(selectedImage, product.name)}

          <div className={styles.productThumbnails}>
            {product.images?.slice(1).map((img, index) => (
              <img
                key={index}
                src={img.signedUrl}
                alt={`Thumbnail ${index + 1}`}
                className={styles.thumbnail}
                onClick={() => handleImageClick(img.signedUrl)}
              />
            ))}
          </div>
        </div>

        <div className={styles.detailsSection}>
          <h1>{product.name}</h1>
          <p>{product.description}</p>
          <p>Price: UGX {parseFloat(product.price).toLocaleString()}</p>

          {parseInt(product.stock_quantity) === 0 && (
            <span className={styles.outOfStockMessage}>
              Product out of stock to be crafted on order
            </span>
          )}

          <button
            onClick={handleAddToCart}
            className={styles.addToCartButton}
            disabled={adding}
          >
            {adding ? 'Adding...' : 'Add to Cart'}
          </button>
        </div>
      </div>

      {similarProducts.length > 0 && (
        <div className={styles.similarProductsSection}>
          <h2>Similar Products</h2>
          <div className={styles.similarProducts}>
            {similarProducts.map((p) => (
              <div
                key={p.id}
                className={styles.similarProductCard}
                onClick={() => {
                  navigate(`/product/${p.id}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {p.images?.[0]?.signedUrl ? (
                  <img
                    src={p.images[0].signedUrl}
                    alt={p.name}
                    className={styles.similarProductImage}
                  />
                ) : (
                  <div className={styles.productPlaceholder}></div>
                )}
                <h3>{p.name}</h3>
                <p>UGX {parseFloat(p.price).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
